import {Component, Input} from '@angular/core';
import {AbstractControl, FormBuilder, FormControl, FormGroup, ValidationErrors, ValidatorFn, Validators} from '@angular/forms';
import {MerchantInfoRequest} from '@npck/af-frontend-api';
import {MERCHANT_IDENTIFIER_PATTERN, MERCHANT_NAME_PATTERN} from '../../../../../../domain/constants';
import {ipValidator} from '../../../../../../infra/ip.validator';

export interface MerchantInfoForm {
    name: FormControl<string>;
    merchantId: FormControl<string>;
    categoryCode: FormControl<string>;
    terminalId: FormControl<string>;
    deviceIp: FormControl<string>;
}

export function merchantInfoAllOrNothingValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
        const group = control as FormGroup<MerchantInfoForm>;
        if (!group?.controls) {
            return null;
        }
        const values = [
            group.controls.name.value,
            group.controls.merchantId.value,
            group.controls.categoryCode.value,
            group.controls.terminalId.value,
            group.controls.deviceIp.value,
        ];
        const filledCount = filledMerchantFieldCount(values);
        return filledCount > 0 && filledCount < values.length ? {merchantInfoAllOrNothing: true} : null;
    };
}

export function createMerchantInfoRequest(merchantForm: FormGroup<MerchantInfoForm>): MerchantInfoRequest | undefined {
    const rawMerchantInfo = merchantForm.getRawValue();
    if (filledMerchantFieldCount(Object.values(rawMerchantInfo)) === 0) {
        return undefined;
    }
    return {
        name: rawMerchantInfo.name.trim(),
        merchantId: rawMerchantInfo.merchantId.trim(),
        categoryCode: rawMerchantInfo.categoryCode.trim(),
        terminalId: rawMerchantInfo.terminalId.trim(),
        deviceIp: rawMerchantInfo.deviceIp.trim()
    };
}

function filledMerchantFieldCount(values: (string | null | undefined)[]): number {
    return values.filter(value => value !== null && value !== undefined && String(value).trim() !== '').length;
}

export function buildMerchantInfoForm(
    formBuilder: FormBuilder,
    required: boolean,
    merchantInfo?: MerchantInfoRequest,
): FormGroup<MerchantInfoForm> {
    const requiredValidators = required ? [Validators.required] : [];
    return formBuilder.group<MerchantInfoForm>({
        name: new FormControl(merchantInfo?.name ?? null, [
            ...requiredValidators,
            Validators.minLength(2),
            Validators.maxLength(255),
            Validators.pattern(MERCHANT_NAME_PATTERN),
        ]),
        merchantId: new FormControl(merchantInfo?.merchantId ?? null, [
            ...requiredValidators,
            Validators.minLength(3),
            Validators.maxLength(50),
            Validators.pattern(MERCHANT_IDENTIFIER_PATTERN),
        ]),
        categoryCode: new FormControl(merchantInfo?.categoryCode ?? null, [...requiredValidators]),
        terminalId: new FormControl(merchantInfo?.terminalId ?? null, [
            ...requiredValidators,
            Validators.minLength(3),
            Validators.maxLength(30),
            Validators.pattern(MERCHANT_IDENTIFIER_PATTERN),
        ]),
        deviceIp: new FormControl(merchantInfo?.deviceIp ?? null, [...requiredValidators, ipValidator()]),
    }, required ? undefined : {validators: [merchantInfoAllOrNothingValidator()]});
}

@Component({
    selector: 'af-payment-information',
    templateUrl: './payment-information.component.html',
    styleUrl: './payment-information.component.scss',
    standalone: false,
})
export class PaymentInformationComponent {
    @Input() form: FormGroup<MerchantInfoForm>;

    Validators = Validators;
    expanded = true;

    toggleExpanded(): void {
        this.expanded = !this.expanded;
        if (!this.expanded) {
            this.form.markAllAsTouched();
        }
    }
}
